import {
  FormArray,
  FormControl,
  FormGroup,
  FormRecord,
  Validators
} from '@angular/forms';
import {
  EsignTemplatesFieldMappingRequestDtoFieldTypeEnum,
  EsignTemplatesFieldMappingRequestDtoSourceEnum
} from 'src/app/shared/vive-api/vive-api-models';

/**
 * One row of the template's field mapping.
 *
 * `source` starts NULL and is `Validators.required`, which is what refuses the
 * save until every discovered field has been accounted for. A field the client
 * genuinely does not use takes `BLANK` — an explicit, recorded decision rather
 * than an absence, so nobody has to guess later whether a gap was deliberate.
 *
 * `isInPdf`, `wasSuggested`, `options` and `takesNoValue` are view state, not
 * payload:
 * - `isInPdf` is false when the mapping names a field the uploaded PDF no
 *   longer has.
 * - `wasSuggested` is true when the source was prefilled from what VIVE
 *   already stamps rather than chosen.
 * - `options` is the export values the FILE offers, which the backend re-reads
 *   from the template on every request and never accepts from a client.
 * - `takesNoValue` marks a widget nothing is ever written into: a signature,
 *   which VIVE adds when the vendor signs, or a widget of a kind the backend
 *   doesn't recognize, such as a push button.
 *
 * `AddEditEsignTemplateModalComponent` strips all four before sending. The
 * backend's `forbidNonWhitelisted` would refuse any of them with a 400.
 *
 * `optionLabels` IS payload — the vendor-facing name for each of those export
 * values, keyed by the value itself rather than by position, because option
 * order is not stable across a file replacement.
 */
export class ESignTemplateFieldForm extends FormGroup<{
  pdfFieldName: FormControl<string>;
  fieldType: FormControl<EsignTemplatesFieldMappingRequestDtoFieldTypeEnum>;
  source: FormControl<null | EsignTemplatesFieldMappingRequestDtoSourceEnum>;
  sourceParam: FormControl<null | string>;
  staticValue: FormControl<null | string>;
  displayLabel: FormControl<null | string>;
  displayOrder: FormControl<number>;
  isRequired: FormControl<boolean>;
  validationRegex: FormControl<null | string>;
  maxLength: FormControl<null | number>;
  optionLabels: FormRecord<FormControl<string>>;
  options: FormControl<string[]>;
  isInPdf: FormControl<boolean>;
  wasSuggested: FormControl<boolean>;
  takesNoValue: FormControl<boolean>;
}> {
  constructor(field: {
    pdfFieldName: string;
    fieldType: EsignTemplatesFieldMappingRequestDtoFieldTypeEnum;
    source?: null | EsignTemplatesFieldMappingRequestDtoSourceEnum;
    sourceParam?: null | string;
    staticValue?: null | string;
    displayLabel?: null | string;
    displayOrder?: number;
    isRequired?: boolean;
    validationRegex?: null | string;
    maxLength?: null | number;
    options?: null | string[];
    optionLabels?: null | Record<string, string>;
    isInPdf?: boolean;
    wasSuggested?: boolean;
    takesNoValue?: boolean;
  }) {
    // One control per option the FILE declares, so a label saved against an
    // export value the PDF has since dropped simply has nowhere to render —
    // rather than offering the vendor a choice the document cannot record.
    const optionLabelControls: Record<string, FormControl<string>> = {};

    for (const option of field.options ?? []) {
      optionLabelControls[option] = new FormControl<string>(
        field.optionLabels?.[option] ?? '',
        { nonNullable: true }
      );
    }

    super({
      pdfFieldName: new FormControl<string>(field.pdfFieldName, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      fieldType:
        new FormControl<EsignTemplatesFieldMappingRequestDtoFieldTypeEnum>(
          field.fieldType,
          { nonNullable: true, validators: [Validators.required] }
        ),
      source:
        new FormControl<null | EsignTemplatesFieldMappingRequestDtoSourceEnum>(
          field.source ?? null,
          { validators: [Validators.required] }
        ),
      sourceParam: new FormControl<null | string>(field.sourceParam ?? null),
      staticValue: new FormControl<null | string>(field.staticValue ?? null),
      displayLabel: new FormControl<null | string>(field.displayLabel ?? null),
      displayOrder: new FormControl<number>(field.displayOrder ?? 0, {
        nonNullable: true
      }),
      isRequired: new FormControl<boolean>(field.isRequired ?? false, {
        nonNullable: true
      }),
      validationRegex: new FormControl<null | string>(
        field.validationRegex ?? null
      ),
      maxLength: new FormControl<null | number>(field.maxLength ?? null),
      optionLabels: new FormRecord<FormControl<string>>(optionLabelControls),
      options: new FormControl<string[]>(field.options ?? [], {
        nonNullable: true
      }),
      isInPdf: new FormControl<boolean>(field.isInPdf ?? true, {
        nonNullable: true
      }),
      wasSuggested: new FormControl<boolean>(field.wasSuggested ?? false, {
        nonNullable: true
      }),
      takesNoValue: new FormControl<boolean>(field.takesNoValue ?? false, {
        nonNullable: true
      })
    });
  }
}

export class ESignTemplateForm extends FormGroup<{
  id: FormControl<undefined | number>;
  ESignProviderId: FormControl<undefined | number>;
  CompanyId: FormControl<undefined | number>;
  TemplateId: FormControl<undefined | string>;
  templateType: FormControl<
    undefined | 'Vendor Agreement' | 'W9' | 'Vendor Agreement & W9'
  >;
  fields: FormArray<ESignTemplateFieldForm>;
  TemplateFile?: FormGroup<{
    filename: FormControl<undefined | string>;
    filetype: FormControl<undefined | string>;
    FileContent: FormGroup<{
      filecontents: FormControl<undefined | string>;
    }>;
  }>;
}> {
  constructor() {
    super({
      id: new FormControl<undefined | number>(undefined, { nonNullable: true }),
      ESignProviderId: new FormControl<undefined | number>(undefined, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      CompanyId: new FormControl<undefined | number>(undefined, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      TemplateId: new FormControl<undefined | string>(undefined, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      templateType: new FormControl<
        undefined | 'Vendor Agreement' | 'W9' | 'Vendor Agreement & W9'
      >(undefined, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      // Empty is not the same as unmapped. An empty array means this template
      // has no mapping of its own and is stamped by VIVE's standard handling —
      // which is every client today — so the modal only sends it once fields
      // have actually been discovered.
      fields: new FormArray<ESignTemplateFieldForm>([])
    });
  }
}
